"use client"

// useBundleTypeBreakdown — fetches Top SKUs + Instances for ONE bundle
// definition in a given analysis period. Backed by
// /api/integrations/shopify/bundle-performance/breakdown.
//
// Lazy-load model: pass `enabled: false` to skip the initial fetch. The
// drill-down uses this so the GET only fires when the user actually opens
// the Top SKUs or Instances tab.
//
// Client-side caching: same module-level ResponseCache pattern as
// useBundlePerformance. Repeat opens of the same drill-down (within TTL)
// hit cache and skip the network round-trip entirely.

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type {
  BundleBreakdownSection,
  BundleTypeBreakdownResponse,
} from "@/lib/shopify/bundles/types"
import { ResponseCache, useResponseCacheGeneration } from "./_response-cache"

/**
 * Optional identity filter for the Instances list — see
 * `BundleInstanceIdentityFilter` server-side. Drives the Bundle × SKU Matrix
 * decompose Sheet: when set, the server filters `instances` to those whose
 * splitter claim rows contain this SKU / family_key. Top SKUs is unaffected.
 *
 * Callers MUST NOT pass this filter for `bundle_sku` strategy bundles — the
 * kit claim row carries the kit's identity, not virtual components', so the
 * filter would zero out every instance. The matrix Sheet gates on
 * `bundle.strategy !== "bundle_sku"`.
 */
export interface BundleTypeBreakdownIdentity {
  group_by: "sku" | "family"
  sku?: string | null
  family_key?: string | null
}

export interface UseBundleTypeBreakdownOptions {
  integrationId: string
  bundleDefinitionId: string
  /** Inclusive ISO-8601. */
  periodStart: string
  /** Exclusive ISO-8601. */
  periodEnd: string
  minimumOrderTotal?: number
  topSkusLimit?: number
  instancesLimit?: number
  /** Identity filter for instances — undefined = no filter (legacy behavior). */
  identity?: BundleTypeBreakdownIdentity
  /**
   * Which response sections to fetch. Drives the lazy per-tab fetch flow in
   * the Bundle Performance drill-down — each tab passes its own section list
   * so opening Top SKUs never pays the cost of fetching period-wide line
   * items for Standalone. Omitted = server default (top_skus + instances)
   * to preserve the matrix decompose Sheet's contract.
   */
  sections?: BundleBreakdownSection[]
  /**
   * When false, the hook idles — no auto-fetch. Flip to true (e.g. when a
   * tab opens) to trigger one. Defaults to true for callers that want
   * standard fetch-on-mount behaviour.
   */
  enabled?: boolean
}

export interface UseBundleTypeBreakdownResult {
  breakdown: BundleTypeBreakdownResponse | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const CACHE_TTL_MS = 5 * 60 * 1000
const cache = new ResponseCache<BundleTypeBreakdownResponse>(CACHE_TTL_MS)

/**
 * Cache key includes auth scope (user + active tenant) FIRST. See
 * use-bundle-performance.ts cacheKey() for rationale: prevents stale-data
 * leak across login/logout boundaries OR tenant-impersonation switches.
 */
function cacheKey(opts: {
  userId: string
  activeTenantId: string
  integrationId: string
  bundleDefinitionId: string
  periodStart: string
  periodEnd: string
  minimumOrderTotal: number
  topSkusLimit: number | undefined
  instancesLimit: number | undefined
  identity: BundleTypeBreakdownIdentity | undefined
  sections: ReadonlyArray<BundleBreakdownSection> | undefined
}): string {
  // Identity is a discriminated triple — flatten into the key so SKU vs
  // Family vs unfiltered each get their own cache slot.
  const identityKey = opts.identity
    ? `${opts.identity.group_by}:${opts.identity.sku ?? ""}:${opts.identity.family_key ?? ""}`
    : ""
  // Sections are sorted before joining so {top_skus,instances} and
  // {instances,top_skus} hit the same cache slot — order shouldn't fragment
  // the cache.
  const sectionsKey = opts.sections
    ? [...opts.sections].sort().join(",")
    : ""
  return [
    opts.userId,
    opts.activeTenantId,
    opts.integrationId,
    opts.bundleDefinitionId,
    opts.periodStart,
    opts.periodEnd,
    opts.minimumOrderTotal,
    opts.topSkusLimit ?? "",
    opts.instancesLimit ?? "",
    identityKey,
    sectionsKey,
  ].join("|")
}

export function useBundleTypeBreakdown({
  integrationId,
  bundleDefinitionId,
  periodStart,
  periodEnd,
  minimumOrderTotal = 0,
  topSkusLimit,
  instancesLimit,
  identity,
  sections,
  enabled = true,
}: UseBundleTypeBreakdownOptions): UseBundleTypeBreakdownResult {
  // Normalise sections to a stable reference so identity-based React deps
  // don't re-fire fetchData when the caller passes a fresh array each render.
  const sectionsKey = sections ? [...sections].sort().join(",") : ""
  const stableSections = useMemo(
    () => (sectionsKey ? (sectionsKey.split(",") as BundleBreakdownSection[]) : undefined),
    [sectionsKey],
  )
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const cacheGeneration = useResponseCacheGeneration()
  const userId = user?.user_id ?? ""
  const activeTenantId = user?.impersonated_tenant_id ?? user?.tenant_id ?? ""
  const [breakdown, setBreakdown] = useState<BundleTypeBreakdownResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Stale-response guard — see use-bundle-performance.ts for rationale. Each
  // fetchData call (including cache hits and invalid-input paths) bumps the
  // counter; setState is only allowed when the captured local seq still
  // matches.
  const requestSeqRef = useRef(0)

  // Drop module cache on logout — defense in depth (see use-bundle-performance).
  useEffect(() => {
    if (!isAuthenticated) cache.clear()
  }, [isAuthenticated])

  const fetchData = useCallback(
    async (useCache: boolean) => {
      const localSeq = ++requestSeqRef.current

      // Same UX rule as useBundlePerformance: if any required input is missing,
      // clear stale data instead of leaving it visible.
      if (
        !integrationId ||
        !bundleDefinitionId ||
        !periodStart ||
        !periodEnd ||
        !isAuthenticated ||
        !userId ||
        !activeTenantId
      ) {
        if (localSeq === requestSeqRef.current) {
          setBreakdown(null)
          setError(null)
          setIsLoading(false)
        }
        return
      }

      const key = cacheKey({
        userId,
        activeTenantId,
        integrationId,
        bundleDefinitionId,
        periodStart,
        periodEnd,
        minimumOrderTotal,
        topSkusLimit,
        instancesLimit,
        identity,
        sections: stableSections,
      })

      if (useCache) {
        const cached = cache.get(key)
        if (cached != null) {
          if (localSeq === requestSeqRef.current) {
            setBreakdown(cached)
            setError(null)
            setIsLoading(false)
          }
          return
        }
      }

      setIsLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({
          integration_id: integrationId,
          bundle_definition_id: bundleDefinitionId,
          period_start: periodStart,
          period_end: periodEnd,
          minimum_order_total: String(minimumOrderTotal),
        })
        if (topSkusLimit != null) params.set("top_skus_limit", String(topSkusLimit))
        if (instancesLimit != null) params.set("instances_limit", String(instancesLimit))
        if (identity) {
          params.set("group_by", identity.group_by)
          if (identity.group_by === "sku" && identity.sku) {
            params.set("sku", identity.sku)
          } else if (identity.group_by === "family" && identity.family_key) {
            params.set("family_key", identity.family_key)
          }
        }
        if (stableSections && stableSections.length > 0) {
          params.set("sections", stableSections.join(","))
        }

        const res = await fetchWithAuth(
          `/api/integrations/shopify/bundle-performance/breakdown?${params}`,
        )
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        const data = (await res.json()) as BundleTypeBreakdownResponse
        // cache.set is intentionally NOT seq-guarded: payload is valid for the
        // request key, and future requests benefit. Only setState is guarded.
        cache.set(key, data, cacheGeneration)
        if (localSeq === requestSeqRef.current) {
          setBreakdown(data)
        }
      } catch (err: unknown) {
        if (localSeq === requestSeqRef.current) {
          const message = err instanceof Error ? err.message : "Failed to load breakdown"
          setError(message)
          setBreakdown(null)
        }
      } finally {
        if (localSeq === requestSeqRef.current) setIsLoading(false)
      }
    },
    [
      cacheGeneration,
      integrationId,
      bundleDefinitionId,
      periodStart,
      periodEnd,
      minimumOrderTotal,
      topSkusLimit,
      instancesLimit,
      identity,
      stableSections,
      isAuthenticated,
      userId,
      activeTenantId,
      fetchWithAuth,
    ],
  )

  const refresh = useCallback(async () => {
    if (
      integrationId &&
      bundleDefinitionId &&
      periodStart &&
      periodEnd &&
      userId &&
      activeTenantId
    ) {
      cache.delete(
        cacheKey({
          userId,
          activeTenantId,
          integrationId,
          bundleDefinitionId,
          periodStart,
          periodEnd,
          minimumOrderTotal,
          topSkusLimit,
          instancesLimit,
          identity,
          sections: stableSections,
        }),
      )
    }
    await fetchData(false)
  }, [
    fetchData,
    userId,
    activeTenantId,
    integrationId,
    bundleDefinitionId,
    periodStart,
    periodEnd,
    minimumOrderTotal,
    topSkusLimit,
    instancesLimit,
    identity,
    stableSections,
  ])

  useEffect(() => {
    if (!enabled) return
    void fetchData(true)
  }, [enabled, fetchData])

  return { breakdown, isLoading, error, refresh }
}
